import { FC, useContext } from 'react';

import Hyperlink from '@ingka/hyperlink';
import SSRIcon from '@ingka/ssr-icon';
import infoMessageIcon from '@ingka/ssr-icon/paths/information-circle';
import phoneIcon from '@ingka/ssr-icon/paths/phone';
import { getLocaleSetting, hasTranslation, t } from '@onlinepayments/payments-library';
import isEqual from 'lodash.isequal';

import ContactInfo from '../../components/ContactInfo';
import DeliveryInfo from '../../components/DeliveryInfo';
import OrderItemList from '../../components/OrderItemList';
import { Box } from '../../components/toolkit';
import { ServiceType } from '../../enums/ServiceType';
import IOrderDetails from '../../interfaces/OrderDetails';
import { OrderDetailsContext } from '../../OrderDetailsContext';

function showShippingContact(order: IOrderDetails) {
  if (
    order?.deliveryServices?.some(
      ({ serviceType }) =>
        serviceType === ServiceType.PickupPointExternal ||
        serviceType === ServiceType.ClickCollectStore ||
        serviceType === ServiceType.PickupPoint,
    )
  ) {
    return false;
  }

  return !isEqual(order.shippingContact, order.billingContact);
}

const OrderContainer: FC = () => {
  const { order, orderItems } = useContext(OrderDetailsContext);
  const { contactUsUrl, customerServicePhoneNumber } = getLocaleSetting();
  const displayContactsUrl = contactUsUrl && hasTranslation('order.contact_us_text_beforepayment');

  return !order || !orderItems ? null : (
    <div className="OrderContainer" data-testid="OrderContainer">
      <Box>
        <h2 className="mb-100">{t('order.order_number')}</h2>
        <p className="">{order.orderNumber}</p>
      </Box>

      <Box>
        <h2 className="mb-100">{t('order_info_header')}</h2>
        {!!orderItems && !!orderItems.length && <OrderItemList items={orderItems} />}
      </Box>

      {!!order.deliveryServices && (
        <Box>
          <h2 className="mb-100">{t('delivery_info_header')}</h2>
          <DeliveryInfo
            shippingContact={order.shippingContact}
            deliveryServices={order.deliveryServices}
          />
        </Box>
      )}

      {showShippingContact(order) && (
        <Box>
          <h2 className="mb-100">{t('order.shipping_contact_header')}</h2>
          <ContactInfo contactInfo={order.shippingContact} />
        </Box>
      )}

      <Box>
        <h2 className="mb-100">{t('customer_info_header')}</h2>
        <ContactInfo contactInfo={order.billingContact} />
      </Box>
      {(hasTranslation('customer_support_info_text') ||
        displayContactsUrl ||
        customerServicePhoneNumber) && (
        <Box bordered={true} className="text-s" data-testid="supportContainer">
          {hasTranslation('customer_support_info_text') && (
            <div className="flex">
              <SSRIcon paths={infoMessageIcon} className="mt-25 mr-50" />
              {t('customer_support_info_text')}
            </div>
          )}
          {displayContactsUrl && (
            <div className="flex mt-50 ml-200" data-testid="contactUsUrl">
              <Hyperlink
                text={t('order.contact_us_text_beforepayment')}
                newWindow={true}
                url={contactUsUrl}
              />
            </div>
          )}
          {!displayContactsUrl && customerServicePhoneNumber && (
            <div className="flex mt-50 ml-200" data-testid="servicePhoneNumber">
              <SSRIcon paths={phoneIcon} className="mr-25" />
              {' ' + customerServicePhoneNumber}
            </div>
          )}
        </Box>
      )}
    </div>
  );
};

export default OrderContainer;
